import { useEmbedContext } from '@thoughtspot/blink-context';
import { isFreeTrialV2ExperienceEnabled } from '@thoughtspot/feature-service';
import { UserState } from '@thoughtspot/session-service';
import { useEffect } from 'react';
import { useMatches } from 'react-router';
import { useAppContext } from '/@contexts/global-app-context';
import { isEmbeddedApp } from '/@utils/embed-base.util';
import { CORE_ROUTES } from '../../core-routes';
import { INSIGHTS_ROUTE_IDS } from '../../insights-routes';
import { Applications } from '../../routes';

export const useOnboardingNavigation = () => {
    const { navService, sessionService } = useAppContext();
    const { isEmbedded } = useEmbedContext();
    const matches = useMatches();
    const currentAppRoute = matches.find(match => match.handle);

    const isLoggedIn = sessionService.isLoggedIn();
    const isUserExpired =
        isLoggedIn && sessionService.getUserState() === UserState.EXPIRED;

    useEffect(() => {
        // Exit out if navService isn't initialized yet.
        if (!navService || !navService.isInitialized) {
            return;
        }
        if (!isLoggedIn || isUserExpired) {
            return;
        }
        const requiresOnboarding =
            !sessionService.hasSeenOnboarding() &&
            !sessionService.isEdgeOnboardingCompleted() &&
            !sessionService.hasSeenFreeTrialOnboarding() &&
            sessionService.isOnboardingEnabled();

        // If a user is already logged in and does not have onboarding completed
        // activation link should still redirect to activation page
        const isOnAccountActivationPage = matches.some(
            match =>
                match.pathname === CORE_ROUTES.AccountActivation.path ||
                match.pathname.startsWith(
                    `${CORE_ROUTES.AccountActivation.path}/`,
                ),
        );

        const isAnswerOrPinboardPage = matches.some(
            match =>
                match.id === INSIGHTS_ROUTE_IDS.SavedAnswerLayout ||
                match.id === INSIGHTS_ROUTE_IDS.Pinboard ||
                match.id === INSIGHTS_ROUTE_IDS.AnswerLayout,
        );

        const shouldShowOnboarding =
            requiresOnboarding &&
            !isAnswerOrPinboardPage &&
            !isEmbeddedApp() &&
            !isEmbedded &&
            !isOnAccountActivationPage;
        if (
            !shouldShowOnboarding ||
            currentAppRoute?.id === Applications.CORE
        ) {
            return;
        }
        if (
            sessionService.isSparkFlowEnabled() &&
            sessionService.isEdgeEnabled()
        ) {
            navService.goToSparkflowOnboarding();
            return;
        }
        if (isFreeTrialV2ExperienceEnabled()) {
            navService.goToGetStarted();
            return;
        }
        if (sessionService.isEdgeEnabled()) {
            navService.goToEdgeOnboarding();
            return;
        }
        navService.goToOnBoarding();
    }, [isLoggedIn, isUserExpired, navService]);
};
